/**
 * Pure, dependency-free sort helper shared by the admin orderline customization
 * panel (`OrderLineCustomizableAssetsComponent`). Kept free of Angular / Vendure
 * imports so it can be unit-tested directly rather than via an inline replica.
 *
 * A "slot" is a built display row that records the source customizable-asset
 * id(s) it represents (a text-frame group buckets several assets, hence the
 * array). `orderedIds` is the settings-order hint returned by
 * `getOrderLineOrderedAssetIds`; it is purely advisory — the rows are built
 * independently, so a slow or failed ordered-ids query must never drop them.
 */
export interface OrderedSlot<T> {
    /** Source asset id(s) this slot represents; a group sorts to its earliest. */
    sortIds: string[];
    /** Built row, or null while an async image-URL fetch is still pending. */
    value: T | null;
}

/**
 * Sort built slots into settings order using the ordered-id hint, dropping any
 * not-yet-filled (null) slots.
 *
 * - A slot's position is the minimum ordered position among its `sortIds`, so a
 *   text-frame group lands at the earliest position of its member assets.
 * - Ids absent from `orderedIds` (empty hint, failed query, or a brand-new asset
 *   the server hasn't ranked) map to Infinity and fall back to a stable numeric
 *   id order, then to original build order — never hidden.
 */
export function sortSlotsByOrderedIds<T>(
    slots: ReadonlyArray<OrderedSlot<T>>,
    orderedIds: ReadonlyArray<string>,
): T[] {
    const posMap = new Map(orderedIds.map((id, i) => [String(id), i]));
    const posOf = (sortIds: string[]): number =>
        Math.min(...sortIds.map(id => posMap.get(id) ?? Infinity));
    return slots
        .map((slot, index) => ({ slot, index }))
        .filter(({ slot }) => slot.value != null)
        .sort((a, b) =>
            (posOf(a.slot.sortIds) - posOf(b.slot.sortIds))
            || ((a.slot.sortIds[0] ?? '').localeCompare(b.slot.sortIds[0] ?? '', undefined, { numeric: true }))
            || (a.index - b.index))
        .map(({ slot }) => slot.value as T);
}
